import type React from "react";
import { useState } from "react";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import SidebarItem from "./SidebarItem";
import { AdvertisementCell } from "../../modules/advertising/tableCells/AdvertisementCell";
import InsightDetailsModal from "./insights/InsightDetailsModal";
import { HiPencilAlt } from "react-icons/hi";
import MetricDisplay, { type Metric } from "./insights/MetricDisplay";
import { useTranslation } from "react-i18next";

interface InsightInfo {
  status: "info" | "success" | "warning";
  content: string;
  metrics?: Metric[];
}

export interface InsightData {
  id: number;
  title: string;
  description?: string;
  created_on: string;
  metadata?: {
    result?: {
      info?: InsightInfo[];
      title?: string;
      status?: string;
    };
  };
  entity_type?: string;
  entity_id?: number;
  entity?: Record<string, unknown>;
  is_viewed?: boolean;
  is_dismissed?: boolean;
  status?: string;
}

interface InsightCardProps {
  insight: InsightData;
  onView?: () => void;
  onDismiss?: () => void;
  onStartNewConversation?: (message: Record<string, unknown>, title: string | undefined) => void;
}

const InsightCard: React.FC<InsightCardProps> = ({ insight, onStartNewConversation }) => {
  const { t } = useTranslation(["chat"]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const insightInfo = insight.metadata?.result?.info?.[0];
  const status = insight.metadata?.result?.status || insight.status;

  const infoMetrics = insightInfo?.metrics || [];

  return (
    <>
      <SidebarItem active={false} createdOn={insight.created_on} badge={status} label="" onClick={() => {}}>
        <VStack width="100%">
          <span className={css({ color: "black", fontSize: "14px", fontWeight: "bold" })}>
            {insight.title || insightInfo?.content || t(($) => $.chat["New Insight"])}
          </span>
          <VStack width="100%" overflowY={"hidden"}>
            <AdvertisementCell
              ad={{
                ...insight.entity,
                ad_id: insight.entity_id || 0,
                ad_name: (insight.entity?.name || "") as string,
              }}
            />
          </VStack>
          {/* Entity display would go here if needed */}
          <HStack className={css({ marginTop: "4px" })} justifyContent="space-between" alignItems="center" width="100%">
            {infoMetrics.map((metric, index) => (
              <MetricDisplay key={index} metric={metric} />
            ))}
          </HStack>
          <HStack
            className={css({ marginTop: "12px" })}
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <button
              title={t(($) => $.chat["Start a conversation about this insight"])}
              className={css({
                cursor: "pointer",
                border: "none",
                color: "#6b7280",
                textDecoration: "underline",
                fontSize: "12px",
                background: "none",
                "&:hover": {
                  color: "#4b5563",
                },
              })}
              onClick={() => {
                if (onStartNewConversation) {
                  const insightJSON = JSON.stringify(insight, null, 2);
                  const message = `The user would like to discuss the following insight: "${insight.title}". Could you provide more details or recommendations regarding this? Here is the full insight data for your reference: ${insightJSON}`;
                  onStartNewConversation(
                    {
                      content: message,
                      role: "system",
                    },
                    t(($) => $.chat['Discussion on: "{title}"'], { title: insight.title }),
                  );
                }
              }}
            >
              <HiPencilAlt />
            </button>
            <button
              onClick={() => setShowDetailsModal(true)}
              className={css({
                cursor: "pointer",
                border: "none",
                color: "#6b7280",
                textDecoration: "underline",
                fontSize: "12px",
                background: "none",
                "&:hover": {
                  color: "#4b5563",
                },
              })}
            >
              {t(($) => $.chat["Full Report"])}
            </button>
          </HStack>
        </VStack>
      </SidebarItem>
      {showDetailsModal && (
        <InsightDetailsModal
          isOpen={showDetailsModal}
          onOpenChange={setShowDetailsModal}
          insight={insight}
          onActionClick={() => {
            // TODO: Implement action handling
          }}
        />
      )}
    </>
  );
};

export default InsightCard;
